/**
 * Sezioni e rotte della parte e-commerce del Magazzino (08 sez. 1). Modulo puro.
 * Le sezioni si aprono con /magazzino?section=<id>; pagina del negozio e collegamento guidato hanno
 * rotte proprie accanto a /magazzino/prodotti/:id.
 */
import type { CommerceProvider } from './types';

export const COMMERCE_SECTIONS = Object.freeze({
  overview: 'ecommerce_overview',
  stores: 'ecommerce_stores',
  products: 'ecommerce_products',
  conflicts: 'ecommerce_conflicts',
  syncs: 'ecommerce_syncs',
} as const);

export type CommerceSection = (typeof COMMERCE_SECTIONS)[keyof typeof COMMERCE_SECTIONS];

export const COMMERCE_SECTION_IDS: readonly CommerceSection[] = Object.values(COMMERCE_SECTIONS);

export const isCommerceSection = (v: unknown): v is CommerceSection =>
  typeof v === 'string' && (COMMERCE_SECTION_IDS as readonly string[]).includes(v);

export const STORE_TABS = ['riepilogo', 'catalogo', 'giacenze', 'clienti', 'ordini', 'funzioni', 'cronologia', 'avanzate'] as const;
export type StoreTab = (typeof STORE_TABS)[number];

export const isStoreTab = (v: unknown): v is StoreTab => typeof v === 'string' && (STORE_TABS as readonly string[]).includes(v);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Id letto dall'URL (job, store): solo un uuid valido, altrimenti null (un link rovinato non rompe le letture). */
export const uuidParam = (v: string | null | undefined): string | null =>
  typeof v === 'string' && UUID_RE.test(v) ? v.toLowerCase() : null;

export const commercePaths = Object.freeze({
  section: (s: CommerceSection, extra?: Record<string, string>) => {
    const q = new URLSearchParams({ section: s, ...(extra ?? {}) });
    return `/magazzino?${q.toString()}`;
  },
  store: (storeId: string, tab: StoreTab = 'riepilogo', extra?: Record<string, string>) => {
    const q = new URLSearchParams({ tab, ...(extra ?? {}) });
    return `/magazzino/negozi/${encodeURIComponent(storeId)}?${q.toString()}`;
  },
  connect: (platform?: CommerceProvider | null) =>
    platform ? `/magazzino/negozi/collega?platform=${platform}` : '/magazzino/negozi/collega',
  reconnect: (storeId: string, platform: CommerceProvider) =>
    `/magazzino/negozi/${encodeURIComponent(storeId)}/collega?platform=${platform}&store=${encodeURIComponent(storeId)}`,
  /** Ordini Clienti filtrati per negozio (08 sez. 12.2). */
  orders: (storeId?: string) => (storeId ? `/magazzino?section=orders&store=${encodeURIComponent(storeId)}` : '/magazzino?section=orders'),
  marketplace: (platform: CommerceProvider) => `/settings?tab=integrations-hub&integration=${platform}`,
});
